import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { AiRecommendationInsight } from '@/types/domain/quotation-comparison';
import { formatCurrency } from '@/lib/utils';
import { Sparkles, CheckCircle2, AlertTriangle, ShieldCheck, Info } from 'lucide-react';

interface AiRecommendationCardProps {
  insight: AiRecommendationInsight;
}

export const AiRecommendationCard: React.FC<AiRecommendationCardProps> = ({ insight }) => {
  return (
    <Card className="border-purple-500/30 rounded-2xl shadow-sm bg-gradient-to-br from-purple-500/5 via-card to-background font-poppins">
      <CardHeader className="pb-3 border-b border-purple-500/20">
        <CardTitle className="font-poppins text-xs font-bold text-purple-700 uppercase tracking-wider flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-purple-600 animate-pulse" />
            <span>AI Procurement Recommendation Insight</span>
          </div>

          <span className="text-[10px] font-extrabold uppercase px-2.5 py-0.5 rounded-full bg-purple-500/10 text-purple-700 border border-purple-500/20">
            Decision Support Only
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        {/* Recommendation Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-xl border border-purple-500/20 bg-purple-500/5">
          <div className="space-y-0.5">
            <span className="text-[10px] font-extrabold text-purple-700 uppercase tracking-wider block">
              AI Recommended Winning Supplier
            </span>
            <span className="text-xl font-extrabold text-foreground block">
              {insight.recommendedVendorName}
            </span>
            <span className="text-xs text-foreground-secondary font-medium block">
              Potential Sourcing Savings:{' '}
              <strong className="text-emerald-600 font-mono font-bold">
                {formatCurrency(insight.potentialSavings, 'INR')}
              </strong>
            </span>
          </div>

          <div className="text-right space-y-1">
            <span className="text-[10px] font-bold text-foreground-muted uppercase block">Confidence Score</span>
            <div className="inline-flex items-center gap-1 text-2xl font-extrabold text-purple-700 font-mono">
              <ShieldCheck className="h-6 w-6 text-purple-600" />
              <span>{insight.confidenceScore}%</span>
            </div>
          </div>
        </div>

        {/* Factors Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
          <div className="space-y-2">
            <span className="font-extrabold text-emerald-700 uppercase text-[11px] flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4" /> Positive Evaluation Factors
            </span>
            <ul className="space-y-1.5">
              {insight.positiveFactors.map((fact, idx) => (
                <li key={idx} className="p-2 rounded-lg border border-emerald-500/20 bg-emerald-500/5 text-foreground-secondary font-semibold">
                  ✓ {fact}
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-2">
            <span className="font-extrabold text-amber-700 uppercase text-[11px] flex items-center gap-1.5">
              <AlertTriangle className="h-4 w-4 text-amber-600" /> Risk Flags & Observations
            </span>
            <ul className="space-y-1.5">
              {insight.riskFlags.map((risk, idx) => (
                <li key={idx} className="p-2 rounded-lg border border-amber-500/20 bg-amber-500/5 text-foreground-secondary font-semibold">
                  ⚠ {risk}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Explainability Callout */}
        <div className="p-3 rounded-xl border border-border bg-card/60 text-xs space-y-1">
          <span className="font-bold text-foreground flex items-center gap-1.5">
            <Info className="h-4 w-4 text-primary" /> AI Explainability & Weighted Scoring Logic
          </span>
          <p className="text-foreground-secondary text-[11px] leading-relaxed font-medium">
            {insight.explainabilityBreakdown}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
